import object from "lodash/object";
import { toastr } from "react-redux-toastr";
import format from "date-fns/format";
import { getAccessToken } from "../../utils/tokens";
import { fetchRequest } from "../../utils/http";
import {
  getHttpProtocol as scheme,
  getAppointmentsServieUrl as appointmentServiceUrl,
} from "../../utils/requests";
import downloadCSV from "../../utils/downloadCSV";
import { logError } from "../../components/Errors";
import {
  postCancelAppointment,
  postChangeAppointmentStaff,
  postMarkNoShow,
  postRescheduleAppointment,
  PostRescheduleAppointmentPayload,
} from "../../apis/appointmentApi";
import {
  setSwitchStaff,
  setSwitchStaffListView,
  updateAppointmentHistoryStatus,
  updateAppointmentListStatus,
  cancelAppointmentListView,
  setRescheduleAppointment,
  changeCancelLoading,
  cancelAppointmentMultiView,
  cancelAppointmentSingleStaff,
} from "./apppointmentActions";
import {
  changeTimeZone,
  convertToUtcString,
  getOffset,
} from "../../utils/dateUtils";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../pages/Appointments/AppointmentsUtils";
import { extractError } from "../../utils/misc";
import { ObjectToBeDefined } from "../../types/types";

export function setAppointmentsError(error: unknown) {
  return async function setError(
    dispatch: unknown,
    getState: () => ObjectToBeDefined,
  ) {
    const state = getState();
    const { data: user } = state.user;
    const { data: client } = state.client;

    logError({
      client: object.get(client, "uuid"),
      user: {
        id: object.get(user, "user_uuid"),
        name: `${object.get(user, "first_name")} ${object.get(
          user,
          "last_name",
        )}`,
      },
      message: error,
      severity: "error",
      feature: "APPOINTMENTS_ERROR",
      request: null,
    });
    toast.error(extractError(error), defaultToastOptions);
    return Promise.resolve();
  };
}

export function changeAppointmentStaff(appointmentId, payload, view: string) {
  return async function changeStaff(dispatch, getState) {
    const state = getState();
    const id =
      appointmentId && appointmentId.uuid ? appointmentId.uuid : appointmentId;
    const { data: client } = state.client;
    try {
      const response = await postChangeAppointmentStaff({
        clientUuid: client.uuid,
        appointmentId: id,
        payload,
      });
      const { message, data } = response;
      // @ts-expect-error - error appeared after installing types for `react-redux-toastr`
      toastr.success(message);
      if (view === "ListView") {
        return dispatch(
          setSwitchStaffListView({
            ...data,
            ...payload,
            uuid: id,
            day: appointmentId.day,
          }),
        );
      } else {
        return dispatch(setSwitchStaff({ ...payload, appointmentId }));
      }
    } catch (error) {
      return dispatch(setAppointmentsError(error));
    }
  };
}

export function markNoShow(
  appointmentId: { uuid: any; day?: any },
  view: string,
) {
  return async function noShow(dispatch, getState) {
    const state = getState();
    const { data: client } = state.client;
    const id =
      appointmentId && appointmentId.uuid ? appointmentId.uuid : appointmentId;
    try {
      await postMarkNoShow({ clientUuid: client.uuid, appointmentId: id });
      if (view === "MemberHistory") {
        return dispatch(
          updateAppointmentHistoryStatus({ appointmentId, status: "NOSHOW" }),
        );
      } else if (view === "ListView") {
        const { day, uuid } = appointmentId;
        return dispatch(
          updateAppointmentListStatus({ day, uuid, status: "NOSHOW" }),
        );
      }
      return toastr.success("Success", "Appointment Marked as No Show");
    } catch (error) {
      return dispatch(setAppointmentsError(error));
    }
  };
}

export function cancelAppointment(appointmentId, view: string) {
  return async function cancel(dispatch, getState) {
    const state = getState();
    dispatch(changeCancelLoading(true));
    const { data: client } = state.client;
    const id =
      appointmentId && appointmentId.uuid ? appointmentId.uuid : appointmentId;
    try {
      await postCancelAppointment({
        clientUuid: client.uuid,
        appointmentUuid: id,
      });
      if (view === "MemberHistory") {
        dispatch(
          updateAppointmentHistoryStatus({
            appointmentId,
            status: "CANCELLED",
          }),
        );
      } else if (view === "ListView") {
        const { day, uuid } = appointmentId;
        dispatch(cancelAppointmentMultiView(id));
        dispatch(cancelAppointmentSingleStaff(id));
        dispatch(cancelAppointmentListView({ day, uuid, status: "CANCELLED" }));
      }
      return toastr.success("Success", "Appointment Cancelled");
    } catch (error) {
      return dispatch(setAppointmentsError(error));
    } finally {
      dispatch(changeCancelLoading(false));
    }
  };
}

export interface RescheduleAppointmentPayload {
  startTime: Date;
  endTime: Date;
  previousDay: string;
  diffInMilliseconds: number;
}

export function rescheduleAppointment(
  appointmentId,
  payload: RescheduleAppointmentPayload,
) {
  return async function reschedule(dispatch, getState) {
    const state = getState();
    const { data: client } = state.client;
    const { previousDay } = payload;
    const timeZone = client.timezone;
    const id =
      appointmentId && appointmentId.uuid ? appointmentId.uuid : appointmentId;
    const postPayload: PostRescheduleAppointmentPayload = {
      startTime: payload.startTime,
      endTime: payload.endTime,
      startTimeZone: timeZone,
      endTimeZone: timeZone,
    };

    try {
      const result = await postRescheduleAppointment({
        clientUuid: client.uuid,
        appointmentUuid: id,
        payload: postPayload,
      });
      const startDate = new Date(result.data.entries[0].start);
      const convertedStartDate = changeTimeZone(startDate, client.timezone);
      toast.success(
        format(convertedStartDate, "'Rescheduled to' MMM d 'at' h:mmaaa"),
        defaultToastOptions,
      );
      const date = changeOffsetAndSetTimeToZero(
        payload.startTime,
        client.timezone,
      );
      return dispatch(
        setRescheduleAppointment({
          date,
          uuid: id,
          previousDay,
          entries: result.data.entries,
        }),
      );
    } catch (error) {
      return dispatch(setAppointmentsError(error));
    }
  };
}

function changeOffsetAndSetTimeToZero(date: Date, timezone: string) {
  const utc = convertToUtcString(date)!;
  const onlyDate = utc?.split("T")[0];
  const offset = getOffset(new Date(date), timezone);
  return `${onlyDate}T00:00:00.000${offset}`;
}

export function appointmentScheduleExport({ from, to }) {
  return async function exportSchedule(dispatch, getState) {
    const token = getAccessToken();
    const client = getState().client.data;
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

    try {
      const response = await fetchRequest({
        method: "POST",
        path: `${scheme}//${appointmentServiceUrl}/appointment_schedule/${client.uuid}/export`,
        options: {
          token,
          headers: { "Content-Type": "application/json" },
        },
        body: {
          from,
          to,
          timeZone,
        },
      });

      if (response && response.ok) {
        const result = await response.json();

        const fileName = `Appointment Schedule Export ${new Date(
          from,
        ).toLocaleDateString()} - ${new Date(to).toLocaleDateString()}`;

        if (result.length) {
          downloadCSV({ data: result, fileName, headers: null });
          // @ts-expect-error - error appeared after installing types for `react-redux-toastr`
          toastr.success("Appointment Schedule Exported");
        } else {
          // @ts-expect-error - error appeared after installing types for `react-redux-toastr`
          toastr.info("No Appointment Schedule within given time range");
        }
      }
    } catch (error) {
      dispatch(setAppointmentsError(error));
      // @ts-expect-error - error appeared after installing types for `react-redux-toastr`
      return toastr.error("Error Exporting Appointment Schedule");
    }
  };
}
